import { useState, useCallback, useEffect, ComponentType, useRef, useMemo } from 'react';
import { ColumnState } from 'ag-grid-community';
import {
  SavedViewsBase as SavedViews,
  IView,
  IData as SavedViewIData,
  ISavedViewProps,
  DefaultError,
  defaultComparator,
  getDefaultFetchSavedViews,
  getDefaultSaveSavedView,
  getDefaultCreateSavedView,
  getDefaultSetDefaultSavedView,
  getDefaultRenameSavedView,
  getDefaultDeleteSavedView,
  getDefaultSetActiveSavedView,
} from '@glu/data-components';
import { useComposedGridContext } from 'ComposedGrid/ComposedGridContext';

export const defaultGridEvents = [
  'columnVisible',
  'columnPinned',
  'columnMoved',
  'filterChanged',
  'paginationChanged',
  'sortChanged',
];

export interface IData extends SavedViewIData {
  /** The columnState of the grid */
  columns?: ColumnState[];
  /** The filter model of the grid */
  filters?: Record<string, any>;
  /** The pagination state of the grid */
  pagination?: {
    pageSize?: number;
  };
}

export interface ISavedViewsAgGridProps {
  /** Error message to show when something goes wrong during deletion, renaming, setting default */
  ErrorOnAction?: ComponentType<{ error: string }>;
  /** Error message to show when something goes wrong during saving or creating a view */
  ErrorOnSave?: ComponentType<{ error: string; onClose(): void }>;
  /** The local storage key used for this instance of saved views. Defaults to myGrid. */
  localStorageKey?: string;
  /**
   * Callback used to fetch the saved views asynchronously. Should return a promise that resolves
   * the save views. By default, it retrieves the saved views from localStorage
   */
  onFetchSavedViews?(): Promise<IView[] | undefined>;
  /** Function used to compare the state of the active view to the saved state of the active view */
  comparator?({
    initialData,
    newData,
  }: {
    initialData: IView['data'];
    newData: IView['data'];
  }): boolean;
  /**
   * Callback used set a view as active. Should return a promise with that resolves the new
   * state of the views. One of the new views should have isActive set to true.
   */
  onSetActiveSavedView?({ view, views }: { view: IView; views: IView[] }): Promise<IView[]>;
  /**
   * Callback used set a view as the default. Should return a promise with that resolves the new
   * state of the views. One of the new views should have isDefault set to true.
   */
  onSetDefaultSavedView?({ view, views }: { view: IView; views: IView[] }): Promise<IView[]>;
  /**
   * Callback used to save the active view. Should return a promise with that resolves the new
   * state of the views.
   */
  onSaveSavedView?({
    data,
    view,
    views,
  }: {
    view: IView;
    data: IData;
    views: IView[];
  }): Promise<IView[]>;
  /**
   * Callback used to rename a view. Should return a promise with that resolves the new
   * state of the views.
   */
  onRenameSavedView?({
    name,
    view,
    views,
  }: {
    name: string;
    view: IView;
    views: IView[];
  }): Promise<IView[]>;
  /**
   * Callback used to delete a view. Should return a promise with that resolves the new
   * state of the views.
   */
  onDeleteSavedView?({ view, views }: { view: IView; views: IView[] }): Promise<IView[]>;
  /**
   * Callback used to create a view. Should return a promise with that resolves the new
   * state of the views.
   */
  onCreateSavedView?({
    data,
    name,
    views,
  }: {
    name: string;
    data: IData;
    views: IView[];
  }): Promise<IView[]>;
}

type TPartialSavedViewProps = Partial<
  Omit<
    ISavedViewProps,
    | 'onCreateSavedView'
    | 'onDeleteSavedView'
    | 'onRenameSavedView'
    | 'onSaveSavedView'
    | 'onSetActiveSavedView'
    | 'onSetDefaultSavedView'
  >
>;

export type TSavedViewsAgGridProps = TPartialSavedViewProps & ISavedViewsAgGridProps;

export const useSavedViewsAgGrid = ({
  localStorageKey = 'myGrid',
  onSetActiveSavedView = getDefaultSetActiveSavedView(localStorageKey),
  onCreateSavedView = getDefaultCreateSavedView(localStorageKey),
  onDeleteSavedView = getDefaultDeleteSavedView(localStorageKey),
  onRenameSavedView = getDefaultRenameSavedView(localStorageKey),
  onSaveSavedView = getDefaultSaveSavedView(localStorageKey),
  onSetDefaultSavedView = getDefaultSetDefaultSavedView(localStorageKey),
  comparator = defaultComparator,
  onFetchSavedViews = getDefaultFetchSavedViews(localStorageKey),
  ErrorOnAction = DefaultError,
  ErrorOnSave = DefaultError,
}: ISavedViewsAgGridProps) => {
  const { gridRef } = useComposedGridContext();
  const [errorOnAction, setErrorOnAction] = useState<string>();
  const [errorOnSave, setErrorOnSave] = useState<string>();
  const [views, setViews] = useState<IView[]>();
  const [activeView, setActiveView] = useState<IView>();
  const initialViewData = useRef<IData>();
  const diverged =
    activeView && initialViewData.current
      ? comparator({ initialData: initialViewData.current, newData: activeView.data })
      : false;
  const [wasJustSaved, setWasJustSaved] = useState(false);

  const sortedViews = useMemo(() => views?.sort((v) => (v.isDefault ? -1 : 1)), [views]);

  const getData = useCallback(
    () => ({
      columns: gridRef.current.columnApi.getColumnState(),
      filters: gridRef.current.api.getFilterModel(),
      pagination: {
        pageSize: gridRef.current.api.paginationGetPageSize(),
      },
    }),
    [gridRef]
  );

  const onActive: ISavedViewProps['onSetActiveSavedView'] = useCallback(
    ({ onResolve, view }) =>
      onSetActiveSavedView({ view, views: views! })
        .then((newViews) => {
          const newActiveView =
            newViews.find((v) => v.isActive) || newViews.find((v) => v.isDefault) || newViews[0];
          initialViewData.current = newActiveView.data;
          setActiveView(newActiveView);
          setViews(newViews);
          setErrorOnAction(undefined);
          setWasJustSaved(false);
          onResolve();
        })
        .catch((error: Error) => {
          setErrorOnAction(error.message);
        }),
    [onSetActiveSavedView, views]
  );

  const onSave = useCallback(() => {
    const data = getData();
    return onSaveSavedView({ view: activeView!, data, views: views! })
      .then((newViews) => {
        initialViewData.current = data;
        setViews(newViews);
        setWasJustSaved(true);
      })
      .catch((error: Error) => {
        setErrorOnSave(error.message);
      });
  }, [activeView, getData, onSaveSavedView, views]);

  const onRename: ISavedViewProps['onRenameSavedView'] = useCallback(
    ({ name, view }) =>
      onRenameSavedView({ view, name, views: views! })
        .then((newViews) => {
          if (activeView!.id === view.id) {
            setActiveView((v) => ({ ...v, name } as IView));
          }
          setViews(newViews);
          setErrorOnAction(undefined);
          setWasJustSaved(false);
        })
        .catch((error: Error) => {
          setErrorOnAction(error.message);
        }),
    [activeView, onRenameSavedView, views]
  );

  const onDelete: ISavedViewProps['onDeleteSavedView'] = useCallback(
    ({ view }) =>
      onDeleteSavedView({ view, views: views! })
        .then((newViews) => {
          if (activeView!.id === view.id) {
            const newActiveView =
              newViews.find((v) => v.isActive) || newViews.find((v) => v.isDefault) || newViews[0];
            initialViewData.current = newActiveView.data;
            setActiveView(newActiveView);
          }
          setViews(newViews);
          setErrorOnAction(undefined);
          setWasJustSaved(false);
        })
        .catch((error: Error) => {
          setErrorOnAction(error.message);
        }),
    [activeView, onDeleteSavedView, views]
  );

  const onCreate: ISavedViewProps['onCreateSavedView'] = useCallback(
    ({ name }) => {
      const data = getData();
      return onCreateSavedView({ name, data, views: views! })
        .then((newViews) => {
          setWasJustSaved(true);
          const newActiveView =
            newViews.find((v) => v.isActive) || newViews.find((v) => v.isDefault) || newViews[0];
          initialViewData.current = newActiveView.data;
          setActiveView(newActiveView);
          setViews(newViews);
        })
        .catch((error: Error) => {
          setErrorOnSave(error.message);
        });
    },
    [getData, onCreateSavedView, views]
  );

  const onSetDefault: ISavedViewProps['onSetDefaultSavedView'] = useCallback(
    ({ view }) =>
      onSetDefaultSavedView({ view, views: views! })
        .then((newViews) => {
          setViews(newViews);
          setErrorOnAction(undefined);
        })
        .catch((error: Error) => {
          setErrorOnAction(error.message);
        }),
    [onSetDefaultSavedView, views]
  );

  const onCloseSaveError = useCallback(() => {
    setErrorOnSave(undefined);
  }, []);

  useEffect(() => {
    onFetchSavedViews().then((fetchedViews) => {
      if (!fetchedViews || !fetchedViews.length) return;
      setViews(fetchedViews);
      const initialActiveView =
        fetchedViews.find((v) => v.isActive) ||
        fetchedViews.find((v) => v.isDefault) ||
        fetchedViews[0];
      initialViewData.current = initialActiveView.data;
      setActiveView(initialActiveView);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStorageKey]);

  useEffect(() => {
    if (activeView?.data.columns) {
      gridRef.current.columnApi.applyColumnState({
        state: activeView.data.columns as ColumnState[],
        applyOrder: true,
      });
    }
    if (activeView?.data.pagination.pageSize) {
      gridRef.current.api.paginationSetPageSize(activeView.data.pagination.pageSize);
    }
    if (activeView?.data.filters) {
      gridRef.current.api.setFilterModel(activeView.data.filters);
    }
  }, [activeView, gridRef]);

  useEffect(() => {
    const currRef = gridRef.current;
    const handleChange = () => {
      setActiveView((v) => ({ ...v, data: getData() } as IView));
      setWasJustSaved(false);
    };
    if (currRef.api) {
      defaultGridEvents.forEach((event) => {
        currRef.api.addEventListener(event, handleChange);
      });
    }
    return () => {
      defaultGridEvents.forEach((event) => {
        currRef.api.removeEventListener(event, handleChange);
      });
    };
  }, [activeView, getData, gridRef]);

  return useMemo(() => {
    return {
      activeView,
      diverged,
      errorOnAction: errorOnAction && <ErrorOnAction error={errorOnAction} />,
      errorOnSave: errorOnSave && <ErrorOnSave error={errorOnSave} onClose={onCloseSaveError} />,
      views: sortedViews,
      wasJustSaved,
      onCreateSavedView: onCreate,
      onDeleteSavedView: onDelete,
      onRenameSavedView: onRename,
      onSaveSavedView: onSave,
      onSetActiveSavedView: onActive,
      onSetDefaultSavedView: onSetDefault,
    };
  }, [
    ErrorOnAction,
    ErrorOnSave,
    activeView,
    diverged,
    errorOnAction,
    errorOnSave,
    onActive,
    onCloseSaveError,
    onCreate,
    onDelete,
    onRename,
    onSave,
    onSetDefault,
    sortedViews,
    wasJustSaved,
  ]);
};

const SavedViewsAgGrid = (props: TSavedViewsAgGridProps) => {
  const {
    activeView,
    diverged,
    errorOnAction,
    errorOnSave,
    onCreateSavedView,
    onDeleteSavedView,
    onRenameSavedView,
    onSaveSavedView,
    onSetActiveSavedView,
    onSetDefaultSavedView,
    views,
    wasJustSaved,
  } = useSavedViewsAgGrid(props);

  if (!views || views.length === 0 || !activeView) {
    return null;
  }

  return (
    <SavedViews
      activeView={activeView}
      diverged={diverged}
      errorOnAction={errorOnAction}
      errorOnSave={errorOnSave}
      views={views}
      wasJustSaved={wasJustSaved}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      onCreateSavedView={onCreateSavedView}
      onDeleteSavedView={onDeleteSavedView}
      onRenameSavedView={onRenameSavedView}
      onSaveSavedView={onSaveSavedView}
      onSetActiveSavedView={onSetActiveSavedView}
      onSetDefaultSavedView={onSetDefaultSavedView}
    />
  );
};

export default SavedViewsAgGrid;
